import {useContext, useState} from "react";
import {
    Typography,
    Box,
    List,
    ListItem,
} from "@mui/material";
import ReviewContext from "../contexts/ReviewContext.tsx";
import ImageCarousel from "./ImageCarousel.tsx";
import {useTranslation} from "react-i18next";
import {IOrder} from "../models/IOrder.ts";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const ReviewList = () => {
    const {t} = useTranslation();

    const {reviews} = useContext(ReviewContext);

    const [imageList, setImageList] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

    if (!reviews) {
        return <Typography>Loading reviews...</Typography>;
    }

    const renderStars = (rating: any) => (
        <Typography component="span">
            {[1, 2, 3, 4, 5].map((i) => (
                <span
                    key={i}
                    style={{
                        color: i <= rating ? "gold" : "lightgray",
                        fontSize: "20px",
                        marginRight: "2px",
                    }}
                >
          ★
        </span>
            ))}
        </Typography>
    );

    const handleImageClick = (images: string[], index: number) => {
        const fullUrls = images.map((img) =>
            img.startsWith("/") ? img : `${VITE_API_BASE_URL}/${img}`
        );
        setImageList(fullUrls);
        setCurrentImageIndex(index);
    };

    const handleClose = () => {
        setImageList([]);
        setCurrentImageIndex(0);
    };

    const handlePrev = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentImageIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
    };

    return (
        <>
            <List>
                {reviews.map((review) => (
                    <ListItem
                        key={review._id}
                        alignItems="flex-start"
                        sx={{
                            borderBottom: "1px solid #eee",
                            paddingY: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                            }}
                        >
                            <Typography variant="subtitle1">
                                {renderStars(review.rating)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {/* eslint-disable-next-line no-constant-binary-expression */}
                                {`${t('REVIEW_BY')}: ${(review.order as IOrder).shippingAddress.name}` || "Anonymous"}
                            </Typography>
                        </Box>

                        <Typography
                            variant="body1"
                            color="text.primary"
                            sx={{marginTop: 1}}
                        >
                            {review.comment}
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                marginTop: 1,
                                alignSelf: "flex-start",
                            }}
                        >
                            {(review.images && review.images.length > 0 &&

                                review.images.map((img, idx) => (
                                    <Box
                                        key={idx}
                                        component="img"
                                        src={img.startsWith("/") ? img : `${VITE_API_BASE_URL}/${img}`}
                                        alt={`Review image ${idx + 1}`}
                                        onClick={() => {
                                            if (review.images?.length > 0)
                                                handleImageClick(
                                                    review.images,
                                                    idx
                                                )
                                        }
                                        }
                                        sx={{
                                            width: 90,
                                            height: 90,
                                            objectFit: "cover",
                                            borderRadius: 1,
                                            cursor: "pointer",
                                        }}
                                    />
                                )))}
                        </Box>
                    </ListItem>
                ))}
            </List>

            <ImageCarousel
                imageList={imageList}
                handleClose={handleClose}
                handlePrev={handlePrev}
                handleNext={handleNext}
                currentImageIndex={currentImageIndex}
            />
        </>
    );
};

export default ReviewList;
